import { administrationApiRtkQuery } from '@chillibean/administration-api-client-sdk';
import { AttentionModal, icons, useToast, DropdownOption } from '@chillibean/component-library';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { handleToastError } from '../../../../../common/errors';
import { AdminHeroBox } from '../../../../../HeroBox/AdminHeroBox/AdminHeroBox';
import { selectInternalResult } from '../../redux/internal.slice';
import { DefinitionModal } from '../modals/DefinitionModal';
import ChapterDefinitionCardsContainer from './ChapterDefinitionCardsContainer';

export function ChapterDefinitionsContainer() {
  // HOOKS
  const { showSuccessToast, showErrorToast } = useToast();
  const { accountId, authenticityToken } = useSelector(selectInternalResult);
  const [modalVisible, setModalVisible] = useState({ definitionModal: false, deleteModal: false });
  const [selectedDefinition, setSelectedDefinition] = useState();
  const {
    useDeleteChapterDefinitionMutation,
    useGetChapterDefinitionsQuery,
    useCreateChapterDefinitionMutation,
    useUpdateChapterDefinitionMutation,
  } = administrationApiRtkQuery;
  const {
    data: chapters,
    error,
    isError,
    isLoading,
    refetch,
  } = useGetChapterDefinitionsQuery({ accountId }, { skip: !accountId });
  const [
    createChapterDefinition,
    {
      error: createError,
      isError: createIsError,
      isLoading: createIsLoading,
      isSuccess: isCreateSuccess,
      reset: resetCreate,
    },
  ] = useCreateChapterDefinitionMutation();

  const [
    updateChapterDefinition,
    {
      error: updateError,
      isError: updateIsError,
      isLoading: updateIsLoading,
      isSuccess: isUpdateSuccess,
      reset: resetUpdate,
    },
  ] = useUpdateChapterDefinitionMutation();
  const [
    deleteChapterDefinition,
    { error: deleteError, isError: deleteIsError, isSuccess: isDeleteSuccess, reset: resetDelete },
  ] = useDeleteChapterDefinitionMutation();

  useEffect(() => {
    if (isError || createIsError || updateIsError || deleteIsError) {
      handleToastError(showErrorToast, error || createError || updateError || deleteError);
    }
  }, [isError, createIsError, updateIsError, deleteIsError]);

  useEffect(() => {
    if (isCreateSuccess) {
      showSuccessToast('Chapter Definition Created');
      resetCreate();
    }
    if (isUpdateSuccess) {
      showSuccessToast('Chapter Definition Updated');
      resetUpdate();
    }
    if (isDeleteSuccess) {
      showSuccessToast('Chapter Definition Deleted');
      resetDelete();
    }
  }, [isCreateSuccess, isUpdateSuccess, isDeleteSuccess]);

  const handleCloseDefinitionModal = () => {
    setSelectedDefinition();
    setModalVisible((prev) => ({ ...prev, definitionModal: false }));
  };

  const handleUpdate = (definition) => {
    setSelectedDefinition(definition);
    setModalVisible((prev) => ({ ...prev, definitionModal: true }));
  };

  const handleCloseDeleteAttentionModal = () => {
    setSelectedDefinition();
    setModalVisible((prev) => ({ ...prev, deleteModal: false }));
  };

  const handleDeleteDropdownClick = (definition) => {
    setSelectedDefinition(definition);
    setModalVisible((prev) => ({ ...prev, deleteModal: true }));
  };

  const handleDeleteChapterDefinition = async () => {
    await deleteChapterDefinition({
      accountId: accountId,
      body: { authenticityToken },
      chapterDefinitionId: selectedDefinition?.id,
    });
    refetch();
    handleCloseDeleteAttentionModal();
  };

  const dropdownOptions = (definition) => {
    return [
      new DropdownOption({
        id: 'edit',
        label: 'Edit',
        onClick: () => {
          handleUpdate(definition);
        },
      }),
      new DropdownOption({
        id: 'delete',
        label: 'Delete',
        onClick: () => handleDeleteDropdownClick(definition),
      }),
    ];
  };

  const handleModalSubmit = async (values) => {
    if (selectedDefinition) {
      await updateChapterDefinition({
        accountId: accountId,
        chapterDefinitionId: selectedDefinition.id,
        body: { authenticityToken, ...values },
      });
    } else {
      await createChapterDefinition({
        accountId: accountId,
        body: { authenticityToken, ...values },
      });
    }
    refetch();
  };

  return (
    <>
      <DefinitionModal
        definition={selectedDefinition}
        requestErrors={selectedDefinition ? updateError : createError}
        requestSuccessful={selectedDefinition ? isUpdateSuccess : isCreateSuccess}
        visible={modalVisible.definitionModal}
        onClose={handleCloseDefinitionModal}
        onSubmit={handleModalSubmit}
        loading={selectedDefinition ? updateIsLoading : createIsLoading}
      />
      <AttentionModal
        onClose={handleCloseDeleteAttentionModal}
        onConfirm={handleDeleteChapterDefinition}
        text="Are you sure you want to delete this Chapter Definition? This action is irreversible."
        title={selectedDefinition?.name}
        visible={modalVisible.deleteModal}
      />
      <ChapterDefinitionsWrapper>
        <BoxWrapper>
          <AdminHeroBox
            button={{
              onClick: () => setModalVisible((prev) => ({ ...prev, definitionModal: true })),
              text: 'Create New',
            }}
            content="Chapters have been created so that longer procedures containing a variety of different sections can be divided up visually on the video timeline, making it easier for the user to see and select the part of the procedure they are trying to assess. Chapters that are created here will appear in a dropdown list on the asset’s media player."
            header="Chapter Definitions"
            icon={icons.outlineTableChart}
            loading={isLoading}
            subHeader="media player"
          />
        </BoxWrapper>
        <ChapterDefinitionCardsContainer
          chapters={chapters}
          dropdownOptions={dropdownOptions}
          handleChapterClick={handleUpdate}
          loading={isLoading}
        />
      </ChapterDefinitionsWrapper>
    </>
  );
}

const BoxWrapper = styled.div`
  display: flex;

  margin-bottom: 32px;
`;

const ChapterDefinitionsWrapper = styled.div`
  height: 100%;
  width: calc(100% - 40px);
  max-width: 900px;

  float: left;

  margin: 20px 40px 0px;
`;

export default ChapterDefinitionsContainer;
